import { Card, Empty, List, Skeleton, Tag, Typography } from "antd";
import { AlertCircle, CheckCircle2, Clock3, Loader2, Play, Square } from "lucide-react";
import type { ManagedResourceType, SchedulerOperation } from "../../../backend/cost-optimizer";

const { Text } = Typography;

const STATUS_COLOR: Record<SchedulerOperation["status"], string> = {
    queued: "gold",
    running: "processing",
    succeeded: "success",
    partial: "warning",
    failed: "error"
};

const RESOURCE_TYPE_LABEL: Record<ManagedResourceType, string> = {
    "ecs-service": "ECS service",
    "ec2-instance": "EC2 instance",
    "eks-node-group": "EKS node group",
    "rds-instance": "RDS instance",
    "rds-cluster": "RDS cluster",
    "auto-scaling-group": "Auto Scaling group"
};

function OperationIcon({ operation }: { operation: SchedulerOperation }) {
    if (operation.status === "running" || operation.status === "queued") return <Loader2 className="optimizer-spin" size={16} />;
    if (operation.status === "failed" || operation.status === "partial") return <AlertCircle size={16} />;
    if (operation.status === "succeeded") return <CheckCircle2 size={16} />;
    return operation.action === "start" ? <Play size={16} /> : <Square size={16} />;
}

function formatTimestamp(value?: string) {
    return value
        ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
        : "Not started";
}

export default function RecentActivity({ operations, loading }: { operations: SchedulerOperation[]; loading: boolean }) {
    return (
        <Card
            className="optimizer-content-card optimizer-activity"
            title={<div><span>Recent activity</span><Text type="secondary">The latest scheduler actions</Text></div>}
        >
            {loading ? <Skeleton active paragraph={{ rows: 3 }} /> : operations.length === 0 ? (
                <Empty image={<Clock3 size={28} />} description="No scheduler actions yet" />
            ) : (
                <List
                    dataSource={operations.slice(0, 8)}
                    renderItem={operation => {
                        const succeeded = operation.results?.filter(result => result.status === "succeeded").length || 0;
                        const failedResults = operation.results?.filter(result => result.status === "failed") || [];
                        const failed = failedResults.length;
                        const hasFailureDetails = Boolean(operation.error || failedResults.length);
                        return (
                            <List.Item key={operation.id}>
                                <div className={`optimizer-activity-icon optimizer-activity-icon--${operation.status}`}>
                                    <OperationIcon operation={operation} />
                                </div>
                                <div className="optimizer-activity-main">
                                    <div>
                                        <strong>{operation.action === "start" ? "Started resources" : "Stopped resources"}</strong>
                                        <Tag color={STATUS_COLOR[operation.status]}>{operation.status}</Tag>
                                    </div>
                                    <Text type="secondary">{operation.source.replace("-", " ")} · {formatTimestamp(operation.requestedAt)}</Text>
                                </div>
                                <div className="optimizer-activity-result">
                                    {operation.results?.length ? (
                                        <><strong>{succeeded}</strong><span>changed</span>{failed > 0 && <Text type="danger">{failed} failed</Text>}</>
                                    ) : <Text type="secondary">Waiting for results</Text>}
                                </div>
                                {hasFailureDetails && (
                                    <div className="optimizer-activity-failures" role="status">
                                        <div className="optimizer-activity-failures__heading">
                                            <AlertCircle aria-hidden="true" size={15} />
                                            <strong>{failedResults.length === 1 ? "Failed resource" : failedResults.length > 1 ? "Failed resources" : "Why it failed"}</strong>
                                        </div>
                                        {operation.error && (
                                            <p className="optimizer-activity-operation-error">{operation.error}</p>
                                        )}
                                        {failedResults.map(result => (
                                            <div className="optimizer-activity-failure" key={`${operation.id}-${result.resourceId}`}>
                                                <div className="optimizer-activity-failure__resource">
                                                    <strong>{result.name || result.resourceId}</strong>
                                                    <span>{RESOURCE_TYPE_LABEL[result.resourceType]}</span>
                                                </div>
                                                {result.name && result.name !== result.resourceId && (
                                                    <code>{result.resourceId}</code>
                                                )}
                                                <p>{result.message || "AWS did not return a reason for this failure."}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </List.Item>
                        );
                    }}
                />
            )}
        </Card>
    );
}
